import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Film,
  Loader2,
  Mic2,
  Newspaper,
  Play,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TwinneeBrand } from '@/components/TwinneeBrand';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import { fetchMurfVoiceCatalog } from '@/lib/murfApi';
import { NarrationVoicePickerDialog } from '@/components/NarrationVoicePickerDialog';
import { TtsProviderStyleSelect } from '@/components/tts/TtsProviderStyleSelect';
import {
  defaultVoiceStyleForProvider,
  normalizeVoiceStyleForProvider,
  shouldShowProviderStyleSelect,
} from '@/lib/ttsProviderStyles';
import {
  buildVideoProjectDocument,
  normalizeVideoSubtitleStyle,
  setPendingVideoProjectNavigation,
} from '@/lib/videoEditorProjectFile';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}/${url}`;
};

const providerLabel = {
  elevenlabs: 'ElevenLabs',
  murf: 'Murf',
  minimax: 'MiniMax',
};

const buildNewsEditorTracks = (project) => {
  let startTime = 0;
  const segments = project?.segments || [];
  if (!segments.length) return { scenes: [], videoTracks: [[]], audioTracks: [[]], musicTracks: [[]] };
  const videoClips = segments.map((segment) => {
    const duration = Number(segment.audio_duration || segment.duration) || 5;
    const clip = {
      id: segment.id,
      name: `Segment ${segment.segment_number || ''}`.trim(),
      sceneId: segment.id,
      videoUrl: segment.video_url || null,
      imageUrl: segment.image_url || null,
      audioUrl: segment.audio_url || null,
      startTime,
      duration,
      playbackDuration: duration,
      trimStart: 0,
      trimEnd: duration,
      muted: Boolean(segment.audio_url),
      sceneText: segment.text || '',
      narrationText: segment.text || '',
    };
    startTime += duration;
    return clip;
  });
  const audioClips = videoClips
    .filter((clip) => clip.audioUrl)
    .map((clip) => ({
      id: `audio-${clip.id}`,
      name: clip.name,
      url: clip.audioUrl,
      startTime: clip.startTime,
      duration: clip.duration,
      sourceDuration: clip.duration,
      volume: 0.8,
      muted: false,
      waveformUrl: `/narration/projects/${project.id}/segments/${clip.id}/waveform`,
    }));
  const scenes = segments.map((segment) => ({
    id: segment.id,
    scene_number: segment.segment_number,
    title: `Segment ${segment.segment_number || ''}`.trim(),
    scene_text: segment.text || '',
    narration_text: segment.text || '',
    video_url: segment.video_url || null,
    image_url: segment.image_url || null,
    audio_url: segment.audio_url || null,
    duration_seconds: Number(segment.audio_duration || segment.duration) || 5,
  }));
  return {
    scenes,
    videoTracks: [videoClips],
    audioTracks: audioClips.length ? [audioClips] : [[]],
    musicTracks: [[]],
  };
};

export const NewsFlowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [newsTopic, setNewsTopic] = useState('');
  const [segmentCount, setSegmentCount] = useState(6);
  const [language, setLanguage] = useState('en');
  const [visualStyle, setVisualStyle] = useState(
    'broadcast news desk, modern LED walls, shallow depth of field',
  );
  const [tone, setTone] = useState('clear, trustworthy, urgent when warranted');
  const [voiceStyle, setVoiceStyle] = useState('newscaster');
  const [ttsProvider, setTtsProvider] = useState('auto');
  const [narratorElevenlabsVoiceId, setNarratorElevenlabsVoiceId] = useState('');
  const [narratorMurfVoiceId, setNarratorMurfVoiceId] = useState('');
  const [narratorMurfLocale, setNarratorMurfLocale] = useState('');
  const [narratorMinimaxVoiceId, setNarratorMinimaxVoiceId] = useState('');
  const [prompterName, setPrompterName] = useState('News anchor');
  const [prompterGender, setPrompterGender] = useState('');
  const [prompterDetails, setPrompterDetails] = useState('');
  const [portraitUrl, setPortraitUrl] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [logoUrl, setLogoUrl] = useState('');
  const [tickerText, setTickerText] = useState('');
  const [adImageUrl, setAdImageUrl] = useState('');
  const [lowerThird, setLowerThird] = useState('');
  const [overlayNotes, setOverlayNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [project, setProject] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const agentHandoff = useMemo(() => readAgentHandoff(location.state), [location.state]);
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    const nav = getIntakeNavPrefill(location.state);
    if (!nav) return;
    if (nav.title) setTitle(nav.title);
    if (nav.newsTopic) setNewsTopic(nav.newsTopic);
    if (nav.language) setLanguage(nav.language);
    if (nav.tone) setTone(nav.tone);
    if (nav.recommended?.segment_count) {
      setSegmentCount(Math.max(2, Math.min(30, Number(nav.recommended.segment_count) || 6)));
    }
    if (readAgentHandoff(location.state)) setHandoffReady(true);
  }, [location.state]);
  const pollRef = useRef(null);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [voiceProvider, setVoiceProvider] = useState('elevenlabs');
  const [murfVoices, setMurfVoices] = useState([]);

  useEffect(() => {
    if (ttsProvider !== 'murf') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchMurfVoiceCatalog(api);
        if (!cancelled) setMurfVoices(data?.voices || []);
      } catch {
        if (!cancelled) setMurfVoices([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ttsProvider]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const openVoicePicker = (provider = ttsProvider) => {
    if (!provider || provider === 'auto') {
      toast.info('Choose ElevenLabs, Murf, or MiniMax first');
      return;
    }
    setVoiceProvider(provider);
    setVoicePickerOpen(true);
  };

  const selectVoice = (voice) => {
    if (voiceProvider === 'elevenlabs') setNarratorElevenlabsVoiceId(voice.voice_id);
    if (voiceProvider === 'murf') {
      setNarratorMurfVoiceId(voice.voice_id);
      setNarratorMurfLocale(voice.locale || voice.effective_locale || narratorMurfLocale);
      setVoiceStyle((prev) => normalizeVoiceStyleForProvider('murf', prev, {
        murfVoices,
        murfVoiceId: voice.voice_id,
        murfLocale: voice.locale || voice.effective_locale || narratorMurfLocale,
        language,
      }));
    }
    if (voiceProvider === 'minimax') setNarratorMinimaxVoiceId(voice.voice_id);
    setVoicePickerOpen(false);
    toast.success(`${providerLabel[voiceProvider]} voice selected`);
  };

  const uploadNewsFile = async (file, kind) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const { data } = await api.post('/narration/news/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
  };

  const onPortraitUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadNewsFile(file, 'image');
      setPortraitUrl(url);
      toast.success('Prompter portrait uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const onAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const audioExt = new Set(['mp3', 'wav', 'm4a', 'aac', 'mpeg']);
    const videoExt = new Set(['mp4', 'mov', 'webm', 'm4v']);
    const kind = audioExt.has(ext) ? 'audio' : videoExt.has(ext) ? 'video' : 'image';
    try {
      const url = await uploadNewsFile(file, kind);
      setAttachments((prev) => [...prev, { url, kind, note: '' }]);
      toast.success('Attachment added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const loadProject = async (id) => {
    const { data } = await api.get(`/narration/projects/${id}`);
    setProject(data);
  };

  const pollJob = useCallback(async (nextJobId, nextProjectId) => {
    try {
      const { data } = await api.get(`/jobs/${nextJobId}`);
      setJob(data);
      if (nextProjectId) await loadProject(nextProjectId);
      if (data.status === 'completed') {
        clearInterval(pollRef.current);
        toast.success('News video ready');
      } else if (data.status === 'failed') {
        clearInterval(pollRef.current);
        toast.error(data.error_message || 'News pipeline failed');
      }
    } catch {
      toast.error('Could not check job status');
    }
  }, []);

  const startNews = async () => {
    if (!title.trim()) {
      toast.error('Add a bulletin title');
      return;
    }
    if (newsTopic.trim().length < 8) {
      toast.error('News topic must be at least 8 characters');
      return;
    }
    setLoading(true);
    setJob(null);
    setProject(null);
    try {
      const payload = {
        title: title.trim(),
        news_topic: newsTopic.trim(),
        segment_count: Number(segmentCount) || 6,
        language,
        visual_style: visualStyle,
        tone,
        voice_style: voiceStyle,
        tts_provider: ttsProvider,
        narrator_elevenlabs_voice_id: narratorElevenlabsVoiceId || null,
        narrator_murf_voice_id: narratorMurfVoiceId || null,
        narrator_murf_locale: narratorMurfLocale || null,
        narrator_minimax_voice_id: narratorMinimaxVoiceId || null,
        prompter: {
          display_name: prompterName.trim() || 'News anchor',
          gender: prompterGender,
          details: prompterDetails,
          portrait_image_url: portraitUrl,
          reference_audio_url: '',
          reference_video_url: '',
        },
        attachments,
        branding: {
          news_logo_url: logoUrl,
          ticker_text: tickerText,
          ad_image_url: adImageUrl,
          lower_third_text: lowerThird,
          overlay_notes: overlayNotes,
        },
      };
      const { data } = await api.post('/narration/news/projects', payload);
      const nextProjectId = data.project.id;
      const nextJobId = data.job_id;
      setProjectId(nextProjectId);
      setJobId(nextJobId);
      setProject(data.project);
      setJob({ id: nextJobId, status: 'pending', progress: 0, message: 'Queued' });
      toast.success('News pipeline started');
      pollRef.current = setInterval(() => pollJob(nextJobId, nextProjectId), 2500);
      await pollJob(nextJobId, nextProjectId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start news pipeline');
    } finally {
      setLoading(false);
    }
  };

  const openInEditor = () => {
    if (!project?.id) {
      toast.error('Project not ready');
      return;
    }
    const editorStoryId = `news-${project.id}`;
    const editorTracks = buildNewsEditorTracks(project);
    const doc = buildVideoProjectDocument({
      storyId: editorStoryId,
      story: {
        id: editorStoryId,
        title: project.title || 'News bulletin',
        tone: project.input?.tone,
        visual_style: project.input?.visual_style,
        input: project.input || {},
        story_type: 'news_video_project',
        news_pack: project.news_pack || {},
      },
      scenes: editorTracks.scenes,
      videoTracks: editorTracks.videoTracks,
      audioTracks: editorTracks.audioTracks,
      musicTracks: editorTracks.musicTracks,
      subtitleTracks: [[]],
      subtitleStyle: normalizeVideoSubtitleStyle(null),
      timelineZoom: 1,
      timelineActive: { image: true, video: true, audio: true, music: true, subtitle: true },
      volume: 0.8,
      isMuted: false,
    });
    setPendingVideoProjectNavigation(doc);
    navigate(`/news/${project.id}/video-editor`);
  };

  const progress = job?.progress || 0;
  const isRunning = job && !['completed', 'failed'].includes(job.status);
  const isComplete = job?.status === 'completed';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="news-flow-page">
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="News flow"
        busy={loading}
        onStart={startNews}
        fields={[
          {
            id: 'title',
            label: 'Bulletin title',
            valid: Boolean(title.trim()),
            render: () => (
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Evening headlines" autoFocus />
            ),
          },
          {
            id: 'topic',
            label: 'News topic',
            hint: 'At least 8 characters.',
            valid: newsTopic.trim().length >= 8,
            render: () => (
              <Textarea value={newsTopic} onChange={(e) => setNewsTopic(e.target.value)} rows={4} placeholder="What is the bulletin about?" />
            ),
          },
        ]}
      />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 bg-white/80 border border-slate-100 shadow-sm">
          <Newspaper className="w-4 h-4 text-sky-600" />
          <span className="text-sm font-semibold text-slate-600">News video flow</span>
        </div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
          Anchor-led <span className="text-sky-600">news bulletin</span>
        </h1>
        <p className="text-slate-600 text-sm sm:text-base max-w-2xl mb-8">
          Define your topic, news prompter (with optional portrait), voice, and reference attachments.{' '}
          <TwinneeBrand className="inline" /> drafts
          anchor dialogue per segment, generates voiceover in your chosen voice, then opens the news editor for logos,
          tickers, and ad overlays.
        </p>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="space-y-2">
              <Label>Bulletin title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Evening headline" />
            </div>
            <div className="space-y-2">
              <Label>News topic & facts</Label>
              <Textarea
                value={newsTopic}
                onChange={(e) => setNewsTopic(e.target.value)}
                rows={5}
                placeholder="What happened, key figures, timeline, and what viewers should know…"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Segments</Label>
                <Input
                  type="number"
                  min={2}
                  max={30}
                  value={segmentCount}
                  onChange={(e) => setSegmentCount(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="ta">Tamil</SelectItem>
                    <SelectItem value="kn">Kannada</SelectItem>
                    <SelectItem value="mr">Marathi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Visual style (B-roll)</Label>
              <Input value={visualStyle} onChange={(e) => setVisualStyle(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Tone</Label>
              <Input value={tone} onChange={(e) => setTone(e.target.value)} />
            </div>
          </div>

          <div className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Mic2 className="w-4 h-4" /> News prompter
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>On-air name</Label>
                <Input value={prompterName} onChange={(e) => setPrompterName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Gender (optional)</Label>
                <Input value={prompterGender} onChange={(e) => setPrompterGender(e.target.value)} placeholder="e.g. female" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Presenter details</Label>
              <Textarea
                value={prompterDetails}
                onChange={(e) => setPrompterDetails(e.target.value)}
                rows={3}
                placeholder="Delivery notes, station call letters, style…"
              />
            </div>
            <div className="space-y-2">
              <Label>Portrait image (for consistent visuals)</Label>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="outline" size="sm" asChild>
                  <label className="cursor-pointer">
                    <Upload className="w-4 h-4 mr-1 inline" />
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={onPortraitUpload} />
                  </label>
                </Button>
                {portraitUrl ? (
                  <img src={resolveMediaUrl(portraitUrl)} alt="" className="h-16 w-16 rounded-lg object-cover border" />
                ) : null}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Voice</Label>
              <div className="flex flex-wrap items-end gap-2">
                <Select
                  value={ttsProvider}
                  onValueChange={(v) => {
                    setTtsProvider(v);
                    if (shouldShowProviderStyleSelect(v)) {
                      setVoiceStyle(defaultVoiceStyleForProvider(v));
                    }
                  }}
                >
                  <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                    <SelectItem value="murf">Murf</SelectItem>
                    <SelectItem value="minimax">MiniMax</SelectItem>
                  </SelectContent>
                </Select>
                <TtsProviderStyleSelect
                  provider={ttsProvider}
                  value={voiceStyle}
                  onChange={setVoiceStyle}
                  murfVoices={murfVoices}
                  murfVoiceId={narratorMurfVoiceId}
                  murfLocale={narratorMurfLocale}
                  language={language}
                  triggerClassName="w-[min(100%,11rem)] h-9 rounded-md border border-input bg-background px-3 text-sm"
                />
                <Button type="button" variant="secondary" size="sm" onClick={() => openVoicePicker()}>
                  Pick voice
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input
                  placeholder="ElevenLabs voice id"
                  value={narratorElevenlabsVoiceId}
                  onChange={(e) => setNarratorElevenlabsVoiceId(e.target.value)}
                />
                <Input
                  placeholder="MiniMax voice id"
                  value={narratorMinimaxVoiceId}
                  onChange={(e) => setNarratorMinimaxVoiceId(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reference attachments</Label>
              <Button type="button" variant="outline" size="sm" asChild>
                <label className="cursor-pointer">
                  <Plus className="w-4 h-4 mr-1 inline" />
                  Add image / audio / video
                  <input type="file" className="hidden" onChange={onAttachmentUpload} />
                </label>
              </Button>
              <ul className="space-y-2">
                {attachments.map((a, i) => (
                  <li key={`${a.url}-${i}`} className="flex items-start gap-2 text-sm border rounded-lg p-2">
                    <span className="text-xs uppercase text-slate-500 shrink-0">{a.kind}</span>
                    <Input
                      className="flex-1 text-xs"
                      value={a.note}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAttachments((prev) => prev.map((x, j) => (j === i ? { ...x, note: v } : x)));
                      }}
                      placeholder="What this reference is for"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="text-sm font-semibold text-slate-700">On-air branding (editable later in editor)</div>
              <div className="space-y-2">
                <Label>News logo URL</Label>
                <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…" />
              </div>
              <div className="space-y-2">
                <Label>Rolling ticker text</Label>
                <Textarea value={tickerText} onChange={(e) => setTickerText(e.target.value)} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Ad / sponsor image URL</Label>
                <Input value={adImageUrl} onChange={(e) => setAdImageUrl(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Lower third text</Label>
                <Input value={lowerThird} onChange={(e) => setLowerThird(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Overlay notes</Label>
                <Textarea value={overlayNotes} onChange={(e) => setOverlayNotes(e.target.value)} rows={2} />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Button
            size="lg"
            className="rounded-full px-8"
            disabled={loading || isRunning}
            onClick={startNews}
          >
            {loading || isRunning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <TwinneeLogo className="w-4 h-4 mr-2" />
                Generate news bulletin
              </>
            )}
          </Button>
          {projectId && (
            <Button variant="outline" size="lg" className="rounded-full" onClick={openInEditor}>
              <Film className="w-4 h-4 mr-2" />
              Open news editor
            </Button>
          )}
        </div>

        {job && (
          <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="flex items-center gap-2 mb-2">
              {isComplete ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Loader2 className="w-5 h-5 animate-spin text-sky-600" />}
              <span className="font-medium text-slate-700">{job.message || job.status}</span>
            </div>
            <Progress value={progress} className="h-2 mb-2" />
            {job.error_message && <p className="text-sm text-red-600">{job.error_message}</p>}
            {jobId && <p className="text-xs text-slate-500">Job: {jobId}</p>}
          </div>
        )}
      </motion.div>

      <NarrationVoicePickerDialog
        open={voicePickerOpen}
        onOpenChange={setVoicePickerOpen}
        provider={voiceProvider}
        language={language}
        onSelectVoice={selectVoice}
      />
    </div>
  );
};
